import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api.js';
import { useSocketEvent } from '../../shared/socket.js';
import { useNotifications } from '../../shared/useNotifications.js';

const KEY = ['notifications', 'centre'];

/**
 * مركز الإشعارات — مصدر واحد للجرس.
 *
 * الخادم يُعيد `{ notifications, unread, open }` لا `{ items }` عمداً: مُعترض
 * api.js يفكّ أي ظرف فيه `items` إلى مصفوفة فيضيع `unread`.
 *
 * التحديث بلا إعادة تحميل: اشتراك على مجال `notifications` (مُوجَّه لغرفة
 * الشركة وحدها) + استطلاع كل 30 ثانية كشبكة أمان إن انقطع المقبس. قراءة
 * القائمة على الخادم تُحدّث التنبيهات من واقع المخزون والجلسات أولاً.
 */
export function useNotificationCentre({ enabled = true } = {}) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEY,
    enabled,
    queryFn: async () => {
      const { data } = await api.get('/notifications', { params: { limit: 30 } });
      return {
        notifications: Array.isArray(data?.notifications) ? data.notifications : [],
        unread: Number(data?.unread) || 0,
        open: Number(data?.open) || 0
      };
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
    retry: false
  });

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: KEY });
  }, [qc]);

  useSocketEvent('notifications', 'notification:new', invalidate);
  useSocketEvent('notifications', 'notification:resolved', invalidate);

  // التنبيهات المحلية القديمة (مخزن zustand المشترك) تبقى معروضة في نفس
  // المركز بدل مركز ثانٍ موازٍ.
  const localItems = useNotifications((s) => s.items);
  const localUnread = useNotifications((s) => s.unreadCount);
  const markLocalRead = useNotifications((s) => s.markAllRead);

  const server = query.data || { notifications: [], unread: 0, open: 0 };

  const local = (localItems || []).map((n) => ({
    _id: `local:${n.id}`,
    key: `local:${n.id}`,
    type: n.type || 'local',
    category: 'local',
    severity: n.severity || 'info',
    status: 'open',
    title: n.title || 'إشعار',
    message: n.message || '',
    raisedAt: new Date(n.time || Date.now()).toISOString(),
    read: !!n.read,
    local: true
  }));

  const notifications = [...server.notifications, ...local]
    .sort((a, b) => new Date(b.raisedAt).getTime() - new Date(a.raisedAt).getTime());

  const markAllRead = useCallback(async () => {
    markLocalRead();
    try {
      await api.post('/notifications/read-all');
    } catch {
      /* الجرس لا يجب أن يُظهر خطأ — المحاولة التالية تُصحّح */
    }
    invalidate();
  }, [invalidate, markLocalRead]);

  const markRead = useCallback(async (id) => {
    if (!id || String(id).startsWith('local:')) return;
    try {
      await api.post(`/notifications/${id}/read`);
    } catch {
      /* تجاهل */
    }
    invalidate();
  }, [invalidate]);

  return {
    notifications,
    unread: server.unread + (Number(localUnread) || 0),
    open: server.open + local.length,
    loading: query.isLoading,
    error: query.error || null,
    refetch: query.refetch,
    markAllRead,
    markRead
  };
}
